import { useMemo, useRef, useCallback, useEffect } from "react";

import { IMapLocationTracker } from "../Features/LocationInfo/types";
import { ILog } from "../Lib/Log/types";


const useWakeLock = (tracker: IMapLocationTracker, log: ILog) => {

    const isSupported = useMemo(() => typeof window !== 'undefined' && 'wakeLock' in navigator, []);
    const sentinal = useRef<WakeLockSentinel | null>(null);

    const acquireLock = useCallback(async () => {

        try {
            sentinal.current = await navigator.wakeLock.request("screen");
            log.info("Acquired wake lock");
        }
        catch (error) {
            log.error("Failed to acquire wake lock", error)
        }

    }, [sentinal, log]);

    const releaseLock = useCallback(async () => {

        try {
            if (sentinal.current !== null) {
                await sentinal.current?.release();
                sentinal.current = null;
                log.info("Released wake lock");
            }
        }
        catch (error) {
            log.error("Failed to release wake lock", error)
        }

    }, [sentinal, log]);

    const updateLock = useCallback(async () => {

        if (!isSupported)
            return;

        releaseLock();

        const isAppActive = document.visibilityState === 'visible';

        if (isAppActive === true && tracker.isTrackingActive === true) {
            acquireLock();
        }

    }, [isSupported, tracker.isTrackingActive, releaseLock, acquireLock]);

    useEffect(() => {     
        updateLock();
    }, [tracker.isTrackingActive, updateLock]);

    useEffect(() => {

        const onVisibilitychange = async () => {
            await updateLock();
        }

        document.addEventListener('visibilitychange', onVisibilitychange)

        return () => {  //Unmount handler
            releaseLock();
            document.removeEventListener('visibilitychange', onVisibilitychange)
        }

    }, [updateLock, releaseLock])

    return {
        isSupported
    };

};

export default useWakeLock;